import { Icon } from "@/components/icons/Icon";
import userImgFallback from "@/img/user-fallback.png";
import { classNames } from "@/utils/classnames";
import { chooseByString, getTailwindColors } from "@/utils/color-util";
import { omap } from "@/utils/object-utils";
import { TeamUser } from "@/utils/teamService";
import { Placement } from "@floating-ui/react-dom-interactions";
import { CubeIcon } from "@heroicons/react/24/outline";
import { TooltipV2 } from "@/components/TooltipV2";

const avatarSizeClasses = {
  xs: "h-[14px] w-[14px] min-h-[14px] min-w-[14px] text-cds-xs",
  sm: "w-5 h-5 min-w-5 min-h-5 text-cds-sm",
  md: "w-6 h-6 min-w-6 min-h-6 text-cds-xs",
  lg: "w-8 h-8 min-w-8 min-h-8 text-cds-lg",
  xl: "w-10 h-10 min-w-10 min-h-10 text-xl",
  "2xl": "w-12 h-12 min-w-12 min-h-12 text-2xl",
};

export type UserAvatarCircleSize = "sm" | "md";

export const userAvatarCircleSizeClasses: Record<UserAvatarCircleSize, string> = {
  sm: "h-[18px] w-[18px] min-h-[18px] min-w-[18px] text-cds-xs",
  md: "h-5 w-5 min-h-5 min-w-5 text-cds-sm",
};

const userAvatarCircleIconSizeClasses: Record<UserAvatarCircleSize, string> = {
  sm: "h-[14px] w-[14px] min-h-[14px] min-w-[14px]",
  md: "h-[14px] w-[14px] min-h-[14px] min-w-[14px]",
};

const iconSizeClasses = {
  xs: "w-3 h-3 min-w-3 min-h-3",
  sm: "h-[14px] w-[14px] min-h-[14px] min-w-[14px]",
  md: "w-5 h-5 min-w-5 min-h-5",
  lg: "w-6 h-6 min-w-6 min-h-6",
  xl: "w-7 h-7 min-w-7 min-h-7",
  "2xl": "w-8 h-8 min-w-8 min-h-8",
};

// Neutral identity glyphs are intentionally quieter than the icon/initial
// treatment used by colored avatars. People and service tokens share this
// scale so switching the glyph never changes its optical footprint.
const neutralIconSizeClasses = {
  xs: "h-2.5 w-2.5 min-h-2.5 min-w-2.5",
  sm: "h-[14px] w-[14px] min-h-[14px] min-w-[14px]",
  md: "h-[14px] w-[14px] min-h-[14px] min-w-[14px]",
  lg: "h-4 w-4 min-h-4 min-w-4",
  xl: "h-5 w-5 min-h-5 min-w-5",
  "2xl": "h-6 w-6 min-h-6 min-w-6",
};

const userColors = getTailwindColors()
  .filter((color) => 400 < color.darkness && color.darkness < 800)
  .map((c) => c.color);

export interface UserAvatarProps {
  user?: TeamUser;
  size?: "xs" | "sm" | "md" | "lg" | "xl" | "2xl";
  /** Optional table-density preset for the circle, independent of surrounding text size. */
  circleSize?: UserAvatarCircleSize;
  className?: string;
  isToken?: boolean;
  isUnknown?: boolean;
  isNone?: boolean;
  noTooltip?: boolean;
  fallback?: string;
  isDependabot?: boolean;
  noneTooltipFallback?: string;
  tooltipPrefix?: string;
  tooltipPlacement?: Placement;
  fallbackAppearance?: "initials" | "neutral-user";
}

/**
 * Circular badge representing a user.
 */
export const UserAvatar: React.FC<UserAvatarProps> = ({
  user,
  size = "md",
  circleSize,
  className,
  isNone,
  fallback,
  isToken,
  isUnknown,
  isDependabot,
  noneTooltipFallback,
  tooltipPlacement,
  tooltipPrefix,
  noTooltip = false,
  fallbackAppearance = "initials",
}) => {
  const imgSize = circleSize ? userAvatarCircleSizeClasses[circleSize] : avatarSizeClasses[size];
  const iconSize = circleSize ? userAvatarCircleIconSizeClasses[circleSize] : iconSizeClasses[size];
  const neutralIconSize = circleSize
    ? userAvatarCircleIconSizeClasses[circleSize]
    : neutralIconSizeClasses[size];

  const deactivated = user?.deactivatedAt != null;
  const userImage = user?.image;
  const userName = omap(user?.name, (name) => (deactivated ? `${name} (deactivated)` : name));
  const userNameFirstLetter = userName?.[0];
  const userId = user?.id;
  const userEmail = omap(user?.email ?? fallback, (email) =>
    deactivated ? `${email} (deactivated)` : email,
  );
  const userEmailFirstLetter = userEmail?.[0];

  const noData = userEmail == null && userName == null;
  const neutralIdentityFallback = (
    <span
      className={classNames(
        "flex items-center justify-center overflow-hidden rounded-full bg-cds-surface-active dark:bg-white/[0.08]",
        imgSize,
        className,
        deactivated && "opacity-50",
      )}
    >
      {isToken ? (
        <CubeIcon
          className={classNames("!text-zinc-300 dark:!text-zinc-600", neutralIconSize)}
          aria-hidden={true}
        />
      ) : isUnknown ? (
        <span
          className={"font-medium leading-none text-zinc-300 dark:text-zinc-600"}
          aria-hidden={true}
        >
          ?
        </span>
      ) : (
        <Icon
          name="user"
          className={classNames("!text-zinc-300 dark:!text-zinc-600", neutralIconSize)}
          aria-hidden={true}
        />
      )}
    </span>
  );

  const content = userImage ? (
    <div className={classNames("relative", imgSize)}>
      {fallbackAppearance === "neutral-user" && neutralIdentityFallback}
      {/* oxlint-disable-next-line jsx-a11y-js/no-noninteractive-element-interactions -- onError handles image loading failure, not user interaction. */}
      <img
        alt={`User: ${userName ?? userEmail ?? userId ?? "unknown"}`}
        src={userImage}
        onError={(e) => {
          if (fallbackAppearance === "neutral-user") {
            e.currentTarget.style.display = "none";
          } else {
            e.currentTarget.src = userImgFallback.src;
          }
        }}
        referrerPolicy="no-referrer"
        className={classNames(
          imgSize,
          "absolute inset-0 rounded-full",
          className,
          deactivated ? "opacity-30" : "",
        )}
      />
    </div>
  ) : fallbackAppearance === "neutral-user" ? (
    neutralIdentityFallback
  ) : (
    <span
      // title={userName ?? userEmail ?? "Unknown"}
      className={classNames(
        "overflow-hidden rounded-full text-center flex justify-center items-center",
        noData ? "border fill-ds-gray-400" : "text-white",
        isDependabot ? "border" : "",
        imgSize,
        className,
        deactivated && "opacity-50",
      )}
      style={{
        background: isToken
          ? "gray"
          : isDependabot
            ? "white"
            : userId
              ? chooseByString(userColors, userId)
              : userEmail
                ? chooseByString(userColors, userEmail)
                : undefined,
      }}
    >
      {isToken ? (
        <CubeIcon className={iconSize} />
      ) : isDependabot ? (
        <Icon name="dependabot" className={iconSize} />
      ) : (
        userNameFirstLetter?.toUpperCase() ??
        userEmailFirstLetter?.toUpperCase() ?? (
          <Icon name="user" className={classNames("text-cds-text-secondary", iconSize)} />
        )
      )}
    </span>
  );

  return (
    <TooltipV2
      disabled={noTooltip}
      style={{ maxWidth: "800px" }}
      content={`${tooltipPrefix ?? ""}${userName ?? userEmail ?? (isNone ? noneTooltipFallback ?? "None" : "Unknown")}`}
      instant
      placement={tooltipPlacement ?? "left"}
    >
      {content}
    </TooltipV2>
  );
};
